// Shared presentational components for wine SKU metrics and critic reviews.
// Used by both MasterProducts and Alerts so the rendering exists in exactly one place.
import React, { CSSProperties } from 'react';
import { X } from 'lucide-react';
import {
    CriticReviewData,
    MetricRow,
    ComparisonRow,
    formatPriceWithDate,
    formatPercentage,
    getPriceIndicator,
} from './wineFormatting';

const styles: Record<string, CSSProperties> = {
    pricingGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '16px',
        minWidth: '600px',
    },
    pricingItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    pricingLabel: {
        fontSize: '11px',
        color: '#6b7280',
        fontWeight: 500,
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    pricingValue: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#1f2937',
        lineHeight: '1.2',
    },
    pricingComparison: {
        fontSize: '12px',
        fontWeight: 600,
        marginTop: '3px',
    },
    priceComparisonGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '8px',
        marginTop: '6px',
        padding: '8px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #e9ecef',
    },
    priceComparisonItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '2px',
    },
    priceComparisonLabel: {
        fontSize: '10px',
        color: '#6b7280',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    priceComparisonValue: {
        fontSize: '12px',
        fontWeight: 600,
    },
    criticReviewsContainer: {
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    criticReviewsInline: {
        marginTop: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px 16px',
    },
    criticReviewItem: {
        fontSize: '13px',
        color: 'var(--claret-600)',
        cursor: 'pointer',
        textDecoration: 'underline',
        lineHeight: '1.5',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
    },
    modalContent: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'auto',
        padding: '24px',
        position: 'relative',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },
    modalHeader: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: '16px',
        paddingRight: '32px',
    },
    modalBody: {
        fontSize: '14px',
        color: '#374151',
        lineHeight: '1.6',
    },
    modalCloseButton: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        width: '32px',
        height: '32px',
        border: 'none',
        backgroundColor: '#f3f4f6',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        fontSize: '20px',
    },
};

// The 5-up reference-price grid (Market / Last Transaction / Highest Bid / Lowest Listing / Wine Searcher).
// align defaults to 'center' (used by the product page); pass 'left' for a left-aligned data block.
export const ReferencePriceGrid: React.FC<{ rows: MetricRow[]; style?: CSSProperties; align?: 'left' | 'center' }> = ({ rows, style, align = 'center' }) => {
    const itemStyle: CSSProperties = align === 'left'
        ? { ...styles.pricingItem, alignItems: 'flex-start', textAlign: 'left' }
        : styles.pricingItem;
    return (
        <div style={{ ...styles.pricingGrid, ...style }}>
            {rows.map((row) => (
                <div key={row.label} style={itemStyle}>
                    <div style={styles.pricingLabel}>{row.label}</div>
                    <div style={styles.pricingValue}>{formatPriceWithDate(row.price, row.date)}</div>
                    {row.comparison !== undefined && row.comparison !== null && (
                        <div style={{ ...styles.pricingComparison, color: getPriceIndicator(row.comparison).color }}>
                            vs {formatPercentage(row.comparison)}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// The "vs Market / vs Wine Searcher / vs Last Trans" comparison pills.
export const PriceComparisons: React.FC<{ rows: ComparisonRow[]; style?: CSSProperties }> = ({ rows, style }) => (
    <div style={{ ...styles.priceComparisonGrid, ...style }}>
        {rows.map((row) => (
            <div key={row.label} style={styles.priceComparisonItem}>
                <div style={styles.priceComparisonLabel}>{row.label}</div>
                <div style={{ ...styles.priceComparisonValue, color: getPriceIndicator(row.percentage || 0).color }}>
                    {formatPercentage(row.percentage)}
                </div>
            </div>
        ))}
    </div>
);

// Clickable list of critic reviews; clicking one calls onSelect (open the modal).
export const CriticReviewsList: React.FC<{
    reviews: CriticReviewData[];
    onSelect: (review: CriticReviewData) => void;
    style?: CSSProperties;
    // inline: lay reviews out horizontally and show just "Critic: score" (no source/date — those
    // are in the review detail modal). Opt-in so other callers (Alerts) keep the full vertical form.
    inline?: boolean;
}> = ({ reviews, onSelect, style, inline }) => {
    if (!reviews || reviews.length === 0) return null;
    return (
        <div style={{ ...(inline ? styles.criticReviewsInline : styles.criticReviewsContainer), ...style }}>
            {reviews.map((review, idx) => {
                const criticName = review.criticName || 'Unknown Critic';
                const criticSource = review.criticSource || '';
                const score = review.score || 'N/A';
                const reviewDate = review.reviewDate
                    ? new Date(review.reviewDate).toLocaleDateString('en-GB')
                    : '';
                return (
                    <div
                        key={review.reviewId || idx}
                        style={styles.criticReviewItem}
                        onClick={() => onSelect(review)}
                    >
                        {inline
                            ? <>{criticName}: {score}</>
                            : <>{criticName} {criticSource && `(${criticSource})`}: {score} {reviewDate && `(${reviewDate})`}</>}
                    </div>
                );
            })}
        </div>
    );
};

// Modal showing a single critic review's full text. Renders nothing when review is null.
export const CriticReviewModal: React.FC<{ review: CriticReviewData | null; onClose: () => void }> = ({ review, onClose }) => {
    if (!review) return null;
    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button style={styles.modalCloseButton} onClick={onClose}>
                    <X size={18} />
                </button>
                <div style={styles.modalHeader}>
                    {review.criticName || 'Critic Review'}
                    {review.criticSource && ` (${review.criticSource})`}
                    {review.score && ` - Score: ${review.score}`}
                </div>
                {review.reviewDate && (
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                        Review Date: {new Date(review.reviewDate).toLocaleDateString('en-GB')}
                    </div>
                )}
                <div
                    style={styles.modalBody}
                    dangerouslySetInnerHTML={{ __html: review.reviewText || 'No review text available.' }}
                />
            </div>
        </div>
    );
};
